// Envelope model: every value has its own random AES-256-GCM data key (DEK);
// the DEK is sealed to the tenant public key (sealed.ts). Rotation re-seals
// DEKs and never touches ciphertext. Only WebCrypto, so this runs in Node,
// Workers, browsers.
import { bufferSource } from './encoding.ts'

const subtle = globalThis.crypto.subtle
const NONCE_BYTES = 12
const KEY_BYTES = 32

export function generateDek(): Uint8Array {
  return globalThis.crypto.getRandomValues(new Uint8Array(KEY_BYTES))
}

async function aesKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== KEY_BYTES) throw new Error('key must be 32 bytes')
  return subtle.importKey('raw', bufferSource(raw), 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ])
}

export async function encryptValue(
  plain: Uint8Array,
  dek: Uint8Array,
): Promise<{ ciphertext: Uint8Array; nonce: Uint8Array }> {
  const nonce = globalThis.crypto.getRandomValues(new Uint8Array(NONCE_BYTES))
  const key = await aesKey(dek)
  const ct = await subtle.encrypt(
    { name: 'AES-GCM', iv: nonce },
    key,
    bufferSource(plain),
  )
  return { ciphertext: new Uint8Array(ct), nonce }
}

export async function decryptValue(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  dek: Uint8Array,
): Promise<Uint8Array> {
  const key = await aesKey(dek)
  const plain = await subtle.decrypt(
    { name: 'AES-GCM', iv: bufferSource(nonce) },
    key,
    bufferSource(ciphertext),
  )
  return new Uint8Array(plain)
}
